import { lazy, Suspense, useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { NumField, CurrencySelect, Money, currencySymbol, moneyText, num } from "./shared";
import { Link } from "wouter";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { NoticeBox } from "@/components/NoticeBox";
import { reportTable } from "@/lib/calc/report";
import type { TableColumn } from "@/lib/calc/table";
import { solveMargin, marginSteps, marginLadder, type LadderRow, type MarginInput, type MarginOutcome } from "./profitMargin";

const CalcReport = lazy(() => import("@/components/calc/CalcReport"));

const TOOL_INFO_CONTENT = {
  what: `Calculates profit margin and markup from your cost and selling price, or works backward to find the price you need to charge for a target margin or markup, or solves from any two of cost, price, profit, margin and markup. Optional shipping and customer-acquisition cost per unit are included in the profit.`,
  how: `Enter your cost and price to see your margin and markup, or switch tabs to start from a target margin, a markup, or any two numbers you know. Add shipping and acquisition cost per unit if you pay them.`,
  tip: `Margin and markup are calculated differently and often confused — margin is profit as a percentage of the selling price, while markup is profit as a percentage of the cost, so the same numbers give two different percentages.`,
};

export default function ProfitMarginCalculator() {
  const [currency, setCurrency] = useState("USD");
  const [tab, setTab] = useState("margin");
  const [cost, setCost] = useState("20");
  const [price, setPrice] = useState("35");
  const [cost2, setCost2] = useState("20");
  const [targetMargin, setTargetMargin] = useState("40");
  const [cost3, setCost3] = useState("20");
  const [markup3, setMarkup3] = useState("75");
  const [shipping, setShipping] = useState("");
  const [acq, setAcq] = useState("");
  const [anyIn, setAnyIn] = useState({ cost: "", price: "35", profit: "", marginPct: "42.9", markupPct: "" });
  const sym = currencySymbol(currency);

  const extras = { shipping: num(shipping), acquisition: num(acq) };
  const hasExtras = extras.shipping > 0 || extras.acquisition > 0;
  const mk = (o: MarginInput) => solveMargin({ ...o, ...extras });
  const c = num(cost), pr = num(price);
  const valid1 = Number.isFinite(c) && Number.isFinite(pr);
  const c2 = num(cost2), m2 = num(targetMargin);
  const valid2 = Number.isFinite(c2) && Number.isFinite(m2);
  const c3 = num(cost3), k3 = num(markup3);
  const valid3 = Number.isFinite(c3) && Number.isFinite(k3);
  const anyKnown = useMemo(() => {
    const o: Record<string, number> = {};
    for (const [k, v] of Object.entries(anyIn)) if (v.trim() !== "") o[k] = num(v);
    return o;
  }, [anyIn]);

  const oneInput: MarginInput = { cost: c, price: pr, ...extras };
  const twoInput: MarginInput = { cost: c2, marginPct: m2, ...extras };
  const threeInput: MarginInput = { cost: c3, markupPct: k3, ...extras };
  const anyInput: MarginInput = { ...anyKnown, ...extras };
  const out1: MarginOutcome | null = valid1 ? mk({ cost: c, price: pr }) : null;
  const out2: MarginOutcome | null = valid2 ? mk({ cost: c2, marginPct: m2 }) : null;
  const out3: MarginOutcome | null = valid3 ? mk({ cost: c3, markupPct: k3 }) : null;
  const outAny: MarginOutcome = mk(anyKnown);
  const active = tab === "margin" ? { out: out1, input: oneInput } : tab === "price" ? { out: out2, input: twoInput } : tab === "markup" ? { out: out3, input: threeInput } : { out: outAny, input: anyInput };
  const pct = (n: number | null) => (n === null ? "—" : `${n < 0 ? "−" : ""}${Math.abs(n).toFixed(1)}%`);

  const { blocked, gate } = useLogToolUsage(true, 3000);

  const result = active.out && active.out.ok ? active.out.result : null;
  const steps = useMemo(() => (result ? marginSteps(active.input, result, (n) => moneyText(n, currency, 2)) : []), [result, currency, active.input.cost, active.input.price, active.input.marginPct, active.input.markupPct, active.input.profit]); // eslint-disable-line react-hooks/exhaustive-deps
  const ladderRows = useMemo(() => (result ? marginLadder(result.totalCost) : []), [result]);
  const resultRows = result ? [
    { item: "Cost per unit", value: result.cost, shown: moneyText(result.cost, currency) },
    ...(hasExtras ? [{ item: "Shipping per unit", value: result.shipping, shown: moneyText(result.shipping, currency) }, { item: "Acquisition cost per unit", value: result.acquisition, shown: moneyText(result.acquisition, currency) }] : []),
    { item: "Selling price", value: result.price, shown: moneyText(result.price, currency) },
    { item: "Profit per unit", value: result.profit, shown: moneyText(result.profit, currency) },
    { item: "Margin", value: result.marginPct === null ? "" : Math.round(result.marginPct * 100) / 100, shown: pct(result.marginPct) },
    { item: "Markup", value: result.markupPct === null ? "" : Math.round(result.markupPct * 100) / 100, shown: pct(result.markupPct) },
  ] : [];
  type ResRow = (typeof resultRows)[number];
  const resColumns: TableColumn<ResRow>[] = [
    { key: "item", header: "Item", get: (r) => r.item },
    { key: "value", header: "Value", get: (r) => r.value, format: (r) => r.shown, align: "right" },
  ];
  const ladderColumns: TableColumn<LadderRow>[] = [
    { key: "margin", header: "Margin", get: (r) => r.marginPct, format: (r) => `${r.marginPct}%`, align: "right" },
    { key: "markup", header: "Markup", get: (r) => Math.round(r.markupPct * 10) / 10, format: (r) => `${r.markupPct.toFixed(1)}%`, align: "right" },
    { key: "price", header: "Price", get: (r) => Math.round(r.price * 100) / 100, format: (r) => moneyText(r.price, currency), align: "right" },
    { key: "profit", header: "Profit per unit", get: (r) => Math.round(r.profit * 100) / 100, format: (r) => moneyText(r.profit, currency), align: "right" },
  ];

  const cards = (o: MarginOutcome | null) => !o ? (
    <div className="grid grid-cols-3 gap-3">{["Profit per unit", "Margin", "Markup"].map((l) => <Card key={l} className="p-4 text-center"><p className="text-xl font-bold text-primary">—</p><p className="text-xs text-muted-foreground mt-1">{l}</p></Card>)}</div>
  ) : !o.ok ? <NoticeBox className="text-xs" testId="pm-message">{o.message}</NoticeBox> : (
    <div className="grid grid-cols-3 gap-3" data-testid="pm-cards">
      <Card className="p-4 text-center"><p className="text-xl font-bold text-primary" data-testid="pm-profit"><Money n={o.result.profit} currency={currency} /></p><p className="text-xs text-muted-foreground mt-1">Profit per unit</p></Card>
      <Card className="p-4 text-center"><p className="text-xl font-bold text-primary" data-testid="pm-margin">{pct(o.result.marginPct)}</p><p className="text-xs text-muted-foreground mt-1">Margin</p></Card>
      <Card className="p-4 text-center"><p className="text-xl font-bold text-primary" data-testid="pm-markup">{pct(o.result.markupPct)}</p><p className="text-xs text-muted-foreground mt-1">Markup{hasExtras ? " (on total cost)" : ""}</p></Card>
    </div>
  );
  const blockedMsg = <p className="text-sm text-center text-muted-foreground py-4">— blocked at today's usage limit —</p>;
  const setAny = (k: keyof typeof anyIn) => (v: string) => setAnyIn((a) => ({ ...a, [k]: v }));
  const anyCount = Object.keys(anyKnown).length;

  return (
    <ToolContainer>
      <div className="flex justify-end mb-3"><CurrencySelect value={currency} onChange={setCurrency} /></div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full h-auto">
          <TabsTrigger value="margin">Find my margin</TabsTrigger>
          <TabsTrigger value="price">Price for target margin</TabsTrigger>
          <TabsTrigger value="markup">Price for markup</TabsTrigger>
          <TabsTrigger value="any">Any two numbers</TabsTrigger>
        </TabsList>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <NumField label={`Shipping per unit (${sym}, optional)`} value={shipping} onChange={setShipping} hint="Counted as a cost in the profit" />
          <NumField label={`Customer acquisition cost per unit (${sym}, optional)`} value={acq} onChange={setAcq} hint="Ad spend per sale, counted as a cost" />
        </div>

        <TabsContent value="margin" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <NumField label={`Cost per unit (${sym})`} value={cost} onChange={setCost} />
            <NumField label={`Selling price (${sym})`} value={price} onChange={setPrice} />
          </div>
          {blocked ? blockedMsg : cards(out1)}
          {out1 && out1.ok && out1.result.marginPct === null && <NoticeBox className="text-xs">Margin needs a selling price above zero; markup needs a cost above zero.</NoticeBox>}
          <p className="text-xs text-muted-foreground">
            Margin = profit ÷ selling price. Markup = profit ÷ cost. They're
            often confused but aren't the same number.
          </p>
        </TabsContent>

        <TabsContent value="price" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <NumField label={`Cost per unit (${sym})`} value={cost2} onChange={setCost2} />
            <NumField label="Target margin (%)" value={targetMargin} onChange={setTargetMargin} error={valid2 && m2 >= 100 ? "A margin of 100% or more is impossible: you can never keep all of the price." : undefined} />
          </div>
          {blocked ? blockedMsg : (
            <Card className="p-6 text-center">
              <p className="text-sm text-muted-foreground mb-1">You need to charge</p>
              <p className="text-3xl font-bold text-primary" data-testid="pm-required-price">
                {out2 && out2.ok ? <Money n={out2.result.price} currency={currency} /> : "—"}
              </p>
              {valid2 && m2 >= 100 && <p className="text-xs text-red-600 mt-1">No price gives a margin of 100% or more.</p>}
              {out2 && out2.ok && hasExtras && <p className="text-xs text-muted-foreground mt-1">Includes shipping and acquisition cost: profit per unit {moneyText(out2.result.profit, currency)}.</p>}
            </Card>
          )}
          {valid2 && m2 < 0 && <NoticeBox className="text-xs">A negative margin means selling below cost, so you lose money on every unit.</NoticeBox>}
          <p className="text-xs text-muted-foreground">Marketplace fees are not included. Use the <Link href="/tools/marketplace-fee-calculator" className="underline">marketplace fee calculator</Link> to add them.</p>
        </TabsContent>

        <TabsContent value="markup" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <NumField label={`Cost per unit (${sym})`} value={cost3} onChange={setCost3} />
            <NumField label="Markup (%)" value={markup3} onChange={setMarkup3} hint="Markup is a percentage of cost: 75% on a 20 cost adds 15" />
          </div>
          {blocked ? blockedMsg : out3 && out3.ok ? (
            <>
              <Card className="p-6 text-center">
                <p className="text-sm text-muted-foreground mb-1">Selling price</p>
                <p className="text-3xl font-bold text-primary" data-testid="pm-markup-price"><Money n={out3.result.price} currency={currency} /></p>
              </Card>
              {cards(out3)}
            </>
          ) : cards(out3)}
        </TabsContent>

        <TabsContent value="any" className="mt-4 space-y-4">
          <p className="text-sm text-muted-foreground">Fill in any two boxes. The other three are worked out. Margin and markup together are not enough: add a cost, price or profit.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <NumField label={`Cost (${sym})`} value={anyIn.cost} onChange={setAny("cost")} />
            <NumField label={`Price (${sym})`} value={anyIn.price} onChange={setAny("price")} />
            <NumField label={`Profit (${sym})`} value={anyIn.profit} onChange={setAny("profit")} />
            <NumField label="Margin (%)" value={anyIn.marginPct} onChange={setAny("marginPct")} />
            <NumField label="Markup (%)" value={anyIn.markupPct} onChange={setAny("markupPct")} />
          </div>
          {blocked ? blockedMsg : anyCount === 2 ? (
            <>
              {outAny.ok && <Card className="p-4 text-sm text-center" data-testid="pm-any-solved">Cost <strong><Money n={outAny.result.cost} currency={currency} /></strong> · Price <strong><Money n={outAny.result.price} currency={currency} /></strong> · Profit <strong><Money n={outAny.result.profit} currency={currency} /></strong></Card>}
              {cards(outAny)}
            </>
          ) : <NoticeBox className="text-xs">{anyCount < 2 ? "Enter two of the five numbers." : "Enter exactly two: clear the others."}</NoticeBox>}
        </TabsContent>
      </Tabs>

      {!blocked && result && (
        <Suspense fallback={<p className="text-xs text-muted-foreground" role="status">Loading working and export...</p>}>
          <CalcReport
            title="Profit margin calculation" tool="profit-margin-calculator" gate={gate} filename="profit-margin.csv" steps={steps}
            summary={`Solved from ${result.solvedFrom}: price ${moneyText(result.price, currency)}, profit ${moneyText(result.profit, currency)}, margin ${pct(result.marginPct)}, markup ${pct(result.markupPct)}`}
            notes={["Margin = profit / price. Markup = profit / total cost. Marketplace fees and taxes are not included. Illustration only."]}
            tables={[
              reportTable({ caption: "Profit margin result", heading: "Result", rows: resultRows, columns: resColumns, rowKey: (r) => r.item }),
              ...(ladderRows.length ? [reportTable({ caption: "Price needed at your total cost for each margin", heading: "Price for each margin at your cost", rows: ladderRows, columns: ladderColumns, rowKey: (r) => r.marginPct })] : []),
            ]}
          />
        </Suspense>
      )}
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
